import React, { useCallback, useState } from "react";
import { Grid, TableHeaderRow, VirtualTable } from "@devexpress/dx-react-grid-bootstrap4";
import styled from "styled-components";
import axios from "http/customAxios";
import { useLoaderState } from "../shared/hooks";
import { useConfig } from "../../lib/config";
import { FullWidthLoader } from "../../components/full-width-loader";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";

export interface Column {
  name: string;
  title: string;
}

export interface QueryResult {
  result: any[];
  errors: any[];
}

const Container = styled.div`
  height: 100vh;
  padding: 20px;
`;

const QueryInput = styled.textarea`
  height: 200px;
  width: 100%;
  border-width: 1px;
  border-color: gray;
  color: black;
`;

const LoaderContainer = styled.div`
  width: 100%;
  height: 400px;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const SendButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;

const SendButton = styled.button`
  padding: 15px;
  margin: 15px 0 15px 15px;
  border: 2px solid black;
  background-color: #efefef;
`;

const GridContainer = styled.div`
  overflow: auto;
  height: calc(100vh - 300px);
  width: 100%;
`;

const getRowId = (row: { __id__: any }) => row.__id__;

async function fetchQueryResultFile(
  queryExecutorApiEndpoint: string,
  queryRawJSON: string
): Promise<BlobPart> {
  const query = JSON.parse(queryRawJSON);
  query.format = "xlsx";
  return (
    await axios.post(`${queryExecutorApiEndpoint}/query`, query, {
      responseType: "blob",
    })
  ).data;
}

async function executeQuery(
  queryExecutorApiEndpoint: string,
  queryRawJSON: string
): Promise<QueryResult> {
  const query = JSON.parse(queryRawJSON);
  const queryResult = (await axios.post(`${queryExecutorApiEndpoint}/query`, query)).data;
  queryResult.result.forEach((it: any, idx: number) => (it.__id__ = idx));
  return queryResult;
}

function downloadFile(rawResult: ArrayBufferView | ArrayBuffer | Blob | string) {
  const filename = "queryResult.xlsx";
  const url = window.URL.createObjectURL(
    new Blob([rawResult], { type: "application/vnd.openxmlformats " })
  );
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `${filename}`);
  document.body.appendChild(link);
  link.click();
}
const placeHolder = `
  Input a JSON like the following:

  1. For a list of propertyTickers:
  {
    "sql": "SELECT * FROM SCHEMA_PLACEHOLDER.reservas WHERE estado = 'reserva' LIMIT 100",
      "propertyTickers": ["propertyTickerA", "propertyTickerA"]
  }
 
  2. For ALL propertyTickers:
  {
    "sql": "SELECT * FROM SCHEMA_PLACEHOLDER.reservas WHERE estado = 'reserva' LIMIT 100",
      "propertyTickers": "*"
  }

  3. For NON propertyTicker related queries:
  {
    "sql": "SELECT * FROM bk_demo_lucic.reservas WHERE estado = 'reserva' LIMIT 100",
  }  
  `;

function QueryExecutor() {
  const [columns, setColumns]: [Column[], any] = useState([]);
  const [query, setQuery] = useState("");
  const [{ loading, loadError, data }, loaderDispatch] = useLoaderState<QueryResult>({
    loading: false,
  });
  const { queryExecutorApiEndpoint } = useConfig();
  const sendQuery = useCallback(
    async function () {
      loaderDispatch({ type: "load_start" });
      try {
        const rawResult: QueryResult = await executeQuery(queryExecutorApiEndpoint, query);
        let columns: Column[] = [];
        if (rawResult.result.length > 0) {
          columns = Object.keys(rawResult.result[0])
            .filter(it => it !== "__id__")
            .map(it => ({
              name: it,
              title: it,
            }));
        }
        setQuery(JSON.stringify(JSON.parse(query), null, 4));
        loaderDispatch({ type: "load_success", data: rawResult });
        setColumns(columns);
      } catch (e) {
        console.error(e);
        loaderDispatch({
          type: "load_error",
          errorMessageId: "serviceError",
        });
      }
    },
    [loaderDispatch, queryExecutorApiEndpoint, query]
  );

  const exportQuery = useCallback(
    async function () {
      loaderDispatch({ type: "load_start" });
      try {
        const rawResult = await fetchQueryResultFile(queryExecutorApiEndpoint, query);
        downloadFile(rawResult);
        setQuery(JSON.stringify(JSON.parse(query), null, 4));
        loaderDispatch({ type: "load_success", data: undefined });
      } catch (e) {
        console.error(e);
        loaderDispatch({
          type: "load_error",
          errorMessageId: "serviceError",
        });
      }
    },
    [loaderDispatch, queryExecutorApiEndpoint, query]
  );

  const editQuery = useCallback((event: any) => setQuery(event.target.value), []);

  return (
    <Container>
      <QueryInput value={query} onChange={editQuery} placeholder={placeHolder} />
      <SendButtonContainer>
        <SendButton onClick={sendQuery}>Execute query</SendButton>
        <SendButton onClick={exportQuery}>Export query result</SendButton>
      </SendButtonContainer>
      {loading && (
        <LoaderContainer>
          <FullWidthLoader />
        </LoaderContainer>
      )}
      {loadError && <div>Error</div>}
      {data && data.errors && data.errors.length > 0 && (
        <div>
          Errors:
          <ul>
            {data.errors.map(err => (
              <li>
                <div>Hotel: {err.__propertyTicker__}</div>
                <div>Message: {err.sqlMessage}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {data && data.result && data.result.length > 0 && (
        <GridContainer id="GridContainer">
          <Grid rows={data.result} columns={columns} getRowId={getRowId}>
            <VirtualTable />
            <TableHeaderRow />
          </Grid>
        </GridContainer>
      )}
    </Container>
  );
}

export default withAuthorizationVerifier(QueryExecutor);
